import { createFileRoute } from "@tanstack/react-router";
import type { RouterContext } from "@/types/router";
import { ExerciseDetail } from "@/components/exercise-detail";
import { exerciseQueryOptions } from "@/queries/use-exercises";
import { useTranslation } from "react-i18next";
import { ResourceError } from "@/components/ui/resource-error";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { MANAGEMENT_ROUTES } from "@/utils/navigation";

interface LoaderParams {
  params: { exerciseId: string };
  context: RouterContext;
}

async function loadExerciseData({ params, context }: LoaderParams) {
  const exercise = await context.queryClient.ensureQueryData(
    exerciseQueryOptions(params.exerciseId),
  );

  return {
    breadcrumb: exercise?.name,
    hasActionButtons: true,
  };
}

function ErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col px-6 lg:my-2">
      <ResourceError
        error={error}
        notFoundMessage={t("pages:exercise.errors.notFound")}
        errorMessage={t("pages:exercise.errors.loadingFailed", {
          message: error.message,
        })}
      />
    </div>
  );
}

export const Route = createFileRoute(
  "/(protected)/management/exercises/$exerciseId/",
)({
  loader: loadExerciseData,
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { exerciseId } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();

  return (
    <ScrollableContainer>
      <div className="flex min-h-full flex-col px-6 lg:py-2">
        <MobilePageTitle
          className="mt-5 mb-4"
          onBack={() => navigate({ to: MANAGEMENT_ROUTES.EXERCISES })}
        >
          {loaderData.breadcrumb}
        </MobilePageTitle>
        <ExerciseDetail exerciseId={exerciseId} />
      </div>
    </ScrollableContainer>
  );
}
